import React, { useState, useEffect } from 'react';
import { FaRuler, FaPlus, FaEdit, FaEye, FaSearch, FaCalendarAlt, FaUser } from 'react-icons/fa';
import tallaService from '../services/tallaService';
import '../styles/InventoryManagement.css';

const TallaManagement = () => {
    // Estados principales
    const [tallas, setTallas] = useState([]);
    const [socios, setSocios] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingTalla, setEditingTalla] = useState(null);
    const [viewTalla, setViewTalla] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedSocia, setSelectedSocia] = useState('');
    
    // Estado del formulario
    const [formData, setFormData] = useState({
        TALLA_CM: '',
        SOCIA_ID: ''
    });
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);

    // Estados para estadísticas
    const [estadisticas, setEstadisticas] = useState({
        total: 0,
        socias: 0,
        esteAño: 0
    });

    // Cargar datos iniciales
    useEffect(() => {
        loadInitialData();
    }, []);

    const loadInitialData = async () => {
        setLoading(true);
        try {
            const [tallasRes, sociosRes, statsRes] = await Promise.all([
                tallaService.getAllTallas(),
                tallaService.getSociosDisponibles(),
                tallaService.getEstadisticas()
            ]);

            if (tallasRes.success) {
                setTallas(tallasRes.data || []);
            }

            if (sociosRes.success) {
                console.log(`✅ Socios cargados: ${sociosRes.data.length} socias disponibles`);
                setSocios(sociosRes.data || []);
            } else {
                console.error('❌ Error al cargar socios:', sociosRes.message);
                setSocios([]);
            }

            if (statsRes.success) {
                setEstadisticas(statsRes.data);
            }

        } catch (err) {
            console.error('Error al cargar datos:', err);
            setSocios([]); // Asegurar que se inicialice como array vacío
        } finally {
            setLoading(false);
        }
    };

    // Validar formulario
    const validateForm = () => {
        const newErrors = {};
        
        if (!formData.TALLA_CM || formData.TALLA_CM <= 0) {
            newErrors.TALLA_CM = 'La talla debe ser mayor a 0';
        }
        
        if (formData.TALLA_CM && (formData.TALLA_CM < 100 || formData.TALLA_CM > 250)) {
            newErrors.TALLA_CM = 'La talla debe estar entre 100 y 250 cm';
        }

        if (!formData.SOCIA_ID && !editingTalla) {
            newErrors.SOCIA_ID = 'Debe seleccionar una socia';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Manejar cambios en el formulario
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        
        // Limpiar error del campo
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    // Manejar envío del formulario
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) return;

        setSubmitting(true);
        try {
            let result;
            if (editingTalla) {
                // Actualizar talla existente
                result = await tallaService.updateTalla(editingTalla.TALLA_ID, formData);
            } else {
                // Crear nueva talla
                result = await tallaService.createTalla(formData);
            }

            if (result.success) {
                alert(result.message);
                await loadInitialData();
                handleCancelForm();
            } else {
                alert('Error: ' + result.message);
            }
        } catch (err) {
            console.error('Error al procesar talla:', err);
            alert('Error al procesar la solicitud');
        } finally {
            setSubmitting(false);
        }
    };

    // Cancelar formulario
    const handleCancelForm = () => {
        setShowForm(false);
        setEditingTalla(null);
        setFormData({
            TALLA_CM: '',
            SOCIA_ID: ''
        });
        setErrors({});
    };

    // Editar talla
    const handleEdit = (talla) => {
        setEditingTalla(talla);
        setFormData({
            TALLA_CM: talla.TALLA_CM || '',
            SOCIA_ID: talla.SOCIA_ID || ''
        });
        setShowForm(true);
        setErrors({});
    };

    // Ver detalles de talla
    const handleView = (talla) => {
        setViewTalla(talla);
    };

    // Filtrar tallas según búsqueda y filtros
    const filteredTallas = tallas.filter(talla => {
        const matchSearch = searchTerm === '' || 
            talla.TALLA_ID.toLowerCase().includes(searchTerm.toLowerCase()) ||
            talla.SOCIA_NOMBRE.toLowerCase().includes(searchTerm.toLowerCase()) ||
            talla.TALLA_CM.toString().includes(searchTerm);
        
        const matchSocia = selectedSocia === '' || talla.SOCIA_ID === selectedSocia;
        
        return matchSearch && matchSocia;
    });

    if (loading) {
        return (
            <div className="inventory-container">
                <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <p>Cargando gestión de tallas...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="inventory-container">
            {/* Header */}
            <div className="page-header">
                <div className="header-left">
                    <h1><FaRuler className="page-icon" />Gestión de Tallas</h1>
                </div>
            </div>

            {/* Estadísticas */}
            <div className="stats-grid">
                <div className="stat-card">
                    <div className="stat-icon total">
                        <FaRuler />
                    </div>
                    <div className="stat-content">
                        <h3>{estadisticas.total}</h3>
                        <p>Total Tallas</p>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon active">
                        <FaUser />
                    </div>
                    <div className="stat-content">
                        <h3>{estadisticas.socias}</h3>
                        <p>Socias Registradas</p>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon pending">
                        <FaCalendarAlt />
                    </div>
                    <div className="stat-content">
                        <h3>{estadisticas.esteAño}</h3>
                        <p>Este Año</p>
                    </div>
                </div>
            </div>

            {/* Controles de búsqueda y acciones */}
            <div className="inventory-controls">
                <div className="search-controls">
                    <div className="search-box">
                        <FaSearch className="search-icon" />
                        <input
                            type="text"
                            placeholder="Buscar por ID, socia o talla..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    
                    <div className="filter-group">
                        <select
                            value={selectedSocia}
                            onChange={(e) => setSelectedSocia(e.target.value)}
                            className="filter-select"
                        >
                            <option value="">Todas las socias</option>
                            {socios.map(socia => (
                                <option key={socia.SOCIA_ID} value={socia.SOCIA_ID}>
                                    {socia.SOCIA_NOMBRE}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="action-buttons">
                    <button className="register-btn" onClick={() => setShowForm(true)}>
                        <FaPlus /> Nueva Talla
                    </button>
                </div>
            </div>

            {/* Tabla de tallas */}
            <div className="table-container">
                <div className="table-header">
                    <h2>Listado de Tallas ({filteredTallas.length})</h2>
                </div>
                
                <div className="table-wrapper">
                    <table className="inventory-table">
                        <thead>
                            <tr>
                                <th>ID</th>
                                <th>Socia</th>
                                <th>Talla (cm)</th>
                                <th>Fecha Registro</th>
                                <th>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredTallas.length > 0 ? (
                                filteredTallas.map((talla) => (
                                    <tr key={talla.TALLA_ID}>
                                        <td>
                                            <span className="talla-id">{talla.TALLA_ID}</span>
                                        </td>
                                        <td>
                                            <div className="socia-info">
                                                <span className="socia-name">{talla.SOCIA_NOMBRE}</span>
                                                <span className="socia-id">{talla.SOCIA_ID}</span>
                                            </div>
                                        </td>
                                        <td>
                                            <span className="talla-value">{talla.TALLA_CM} cm</span>
                                        </td>
                                        <td>
                                            <span className="fecha-badge">{talla.FECHA_REGISTRO_FORMATTED}</span>
                                        </td>
                                        <td>
                                            <div className="action-buttons-inline">
                                                <button
                                                    className="btn-icon view"
                                                    onClick={() => handleView(talla)}
                                                    title="Ver detalles"
                                                >
                                                    <FaEye />
                                                </button>
                                                <button
                                                    className="btn-icon edit"
                                                    onClick={() => handleEdit(talla)}
                                                    title="Editar talla"
                                                >
                                                    <FaEdit />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="5" className="no-data">
                                        <div className="no-data-message">
                                            <FaRuler className="no-data-icon" />
                                            <p>No se encontraron tallas registradas</p>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Modal de formulario */}
            {showForm && (
                <div className="modal">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h2>
                                <FaRuler style={{ marginRight: '0.5rem' }} />
                                {editingTalla ? 'Editar Talla' : 'Registrar Nueva Talla'}
                            </h2>
                            <button onClick={handleCancelForm} className="close-btn">×</button>
                        </div>
                        
                        <div className="modal-body">
                            <form onSubmit={handleSubmit}>
                                {!editingTalla && (
                                    <div className="form-group">
                                        <label htmlFor="SOCIA_ID">
                                            <FaUser style={{ marginRight: '0.5rem' }} />
                                            Socia
                                        </label>
                                        <select
                                            id="SOCIA_ID"
                                            name="SOCIA_ID"
                                            value={formData.SOCIA_ID}
                                            onChange={handleChange}
                                            className={errors.SOCIA_ID ? 'error' : ''}
                                            disabled={submitting}
                                        >
                                            <option value="">Seleccionar socia...</option>
                                            {socios && socios.length > 0 ? (
                                                socios.map(socia => (
                                                    <option key={socia.SOCIA_ID} value={socia.SOCIA_ID}>
                                                        {socia.SOCIA_NOMBRE} - {socia.CEDULA}
                                                    </option>
                                                ))
                                            ) : (
                                                <option disabled style={{color: '#999', fontStyle: 'italic'}}>
                                                    {loading ? 'Cargando socias...' : 'No hay socias disponibles'}
                                                </option>
                                            )}
                                        </select>
                                        {errors.SOCIA_ID && <span className="error-message">{errors.SOCIA_ID}</span>}
                                    </div>
                                )}

                                {editingTalla && (
                                    <div className="form-group">
                                        <label>Socia</label>
                                        <input
                                            type="text"
                                            value={`${editingTalla.SOCIA_NOMBRE} (${editingTalla.SOCIA_ID})`}
                                            disabled
                                            className="readonly-input"
                                        />
                                        <small className="form-note">No se puede cambiar la socia en modo edición</small>
                                    </div>
                                )}

                                <div className="form-group">
                                    <label htmlFor="TALLA_CM">
                                        <FaRuler style={{ marginRight: '0.5rem' }} />
                                        Talla (cm)
                                    </label>
                                    <input
                                        type="number"
                                        id="TALLA_CM"
                                        name="TALLA_CM"
                                        value={formData.TALLA_CM}
                                        onChange={handleChange}
                                        placeholder="Ej: 165"
                                        min="100"
                                        max="250"
                                        className={errors.TALLA_CM ? 'error' : ''}
                                        disabled={submitting}
                                    />
                                    {errors.TALLA_CM && <span className="error-message">{errors.TALLA_CM}</span>}
                                    <small className="form-note">Ingrese la talla en centímetros (100-250 cm)</small>
                                </div>
                            </form>
                        </div>

                        <div className="modal-footer">
                            <button 
                                type="button" 
                                className="btn-secondary" 
                                onClick={handleCancelForm}
                                disabled={submitting}
                            >
                                Cancelar
                            </button>
                            <button 
                                type="submit" 
                                className="btn-primary"
                                onClick={handleSubmit}
                                disabled={submitting}
                            >
                                {submitting ? (
                                    <>
                                        <i className="fas fa-spinner fa-spin" style={{ marginRight: '0.5rem' }}></i>
                                        {editingTalla ? 'Actualizando...' : 'Registrando...'}
                                    </>
                                ) : (
                                    <>
                                        {editingTalla ? 'Actualizar' : 'Registrar'} Talla
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de vista de detalles */}
            {viewTalla && (
                <div className="modal">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h2>
                                <FaEye style={{ marginRight: '0.5rem' }} />
                                Detalles de Talla
                            </h2>
                            <button onClick={() => setViewTalla(null)} className="close-btn">×</button>
                        </div>
                        
                        <div className="modal-body">
                            <div className="view-details">
                                <div className="detail-group">
                                    <h3>Información de la Talla</h3>
                                    <div className="detail-grid">
                                        <div className="detail-item">
                                            <strong>ID:</strong>
                                            <span className="talla-id-large">{viewTalla.TALLA_ID}</span>
                                        </div>
                                        <div className="detail-item">
                                            <strong>Talla:</strong>
                                            <span className="talla-value-large">{viewTalla.TALLA_CM} cm</span>
                                        </div>
                                        <div className="detail-item">
                                            <strong>Fecha de Registro:</strong>
                                            <span>{viewTalla.FECHA_REGISTRO_FORMATTED}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="detail-group">
                                    <h3>Información de la Socia</h3>
                                    <div className="detail-grid">
                                        <div className="detail-item">
                                            <strong>ID Socia:</strong>
                                            <span>{viewTalla.SOCIA_ID}</span>
                                        </div>
                                        <div className="detail-item">
                                            <strong>Nombre:</strong>
                                            <span>{viewTalla.SOCIA_NOMBRE}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="modal-footer">
                            <button 
                                type="button" 
                                className="btn-secondary" 
                                onClick={() => setViewTalla(null)}
                            >
                                Cerrar
                            </button>
                            <button 
                                type="button" 
                                className="btn-primary"
                                onClick={() => {
                                    setViewTalla(null);
                                    handleEdit(viewTalla);
                                }}
                            >
                                <FaEdit style={{ marginRight: '0.5rem' }} />
                                Editar Talla
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TallaManagement;